/**
 * useStockfishAnalysisManager.js - React hook for Stockfish analysis integration
 * Listens to currentMoveIdx changes and coordinates with StockfishAnalysisManager
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import analysisManager from '../util/StockfishAnalysisManager';

const useStockfishAnalysisManager = ({
  currentMoveIdx,
  moves,
  currentFEN,
  dispatch,
  gameState = null,
  enabled = true
}) => {
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const lastProcessedMoveRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Helper function to check if analysis data matches the current FEN
  const analysisMatchesCurrentFEN = useCallback((analysisData, currentFEN) => {
    if (!analysisData || !currentFEN) return false;
    
    // Check if the analysis data is for this specific position
    const analysisFEN = analysisData.fen || analysisData.positionFEN;
    if (!analysisFEN) return false;
    
    // Compare the FENs, but ignore the move counters (last 2 fields)
    // This ensures we're comparing the actual board positions
    const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
    const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');
    
    return currentFENBase === analysisFENBase;
  }, []);
  
  // Function to get move object from moves array or start position variations
  const getMoveObject = useCallback((moveId, gameState = null) => {
    if (!moveId || moveId === '-1') {
      return null;
    }
    
    // ENHANCED: Handle start position variations
    if (moveId.startsWith('-1|')) {
      // This is a start position variation move
      const match = moveId.match(/^-1\|(\d+):(\d+)$/);
      if (!match || !gameState?.startPosition?.variations) {
        return null;
      }
      
      const varIdx = parseInt(match[1], 10);
      const moveIdx = parseInt(match[2], 10);
      
      if (varIdx < gameState.startPosition.variations.length &&
          gameState.startPosition.variations[varIdx] &&
          moveIdx < gameState.startPosition.variations[varIdx].length) {
        return gameState.startPosition.variations[varIdx][moveIdx];
      }
      
      return null;
    }
    
    if (!moves) {
      return null;
    }
    
    // Handle main line moves
    if (!moveId.includes('|')) {
      const idx = parseInt(moveId, 10);
      return (idx >= 0 && idx < moves.length) ? moves[idx] : null;
    }
    
    // Handle variation moves
    const parts = moveId.split('|');
    let currentNode = null;
    
    // Start with main line move
    const mainIdx = parseInt(parts[0], 10);
    if (mainIdx >= 0 && mainIdx < moves.length) {
      currentNode = moves[mainIdx];
      
      // Navigate through variations
      for (let i = 1; i < parts.length; i++) {
        if (!parts[i].includes(':')) continue;
        
        const [varIdx, moveIdx] = parts[i].split(':').map(Number);
        
        if (i < parts.length - 1) {
          // Navigate to intermediate node
          if (currentNode.variations && 
              varIdx < currentNode.variations.length &&
              moveIdx < currentNode.variations[varIdx].length) {
            currentNode = currentNode.variations[varIdx][moveIdx];
          } else {
            return null;
          }
        } else {
          // Get final move
          if (currentNode.variations && 
              varIdx < currentNode.variations.length &&
              moveIdx < currentNode.variations[varIdx].length) {
            return currentNode.variations[varIdx][moveIdx];
          }
        }
      }
    }
    
    return null;
  }, [moves]);
  
  // Function to update move with analysis
  const updateMoveWithAnalysis = useCallback((moveId, updates) => {
    if (!dispatch || !moveId || moveId === '-1') {
      return;
    }
    
    // console.log(`🔍 useStockfishAnalysisManager: Updating analysis for move ${moveId}`, { updates });
    
    // ENHANCED: Handle start position variations differently
    if (moveId.startsWith('-1|')) {
      // This is a start position variation move - use special dispatch action
      // console.log(`🎯 useStockfishAnalysisManager: Storing analysis in start position variation for ${moveId}`);
      try {
        dispatch({
          type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS',
          payload: {
            moveId,
            properties: updates
          }
        });
      } catch (error) {
        console.error('❌ useStockfishAnalysisManager: Error updating start position variation move:', error);
      }
    } else {
      // Regular move - use existing logic
      try {
        dispatch({
          type: 'UPDATE_MOVE_PROPERTIES',
          payload: {
            moveId,
            properties: updates
          }
        });
      } catch (error) {
        console.error('❌ useStockfishAnalysisManager: Error updating move:', error);
      }
    }
  }, [dispatch]);
  
  // Analysis listener for real-time updates
  const analysisListener = useCallback((analysis, moveId) => {
    if (!mountedRef.current) return;
    
    // ENHANCED: Handle start position variations more carefully
    // Skip analysis updates for starting position variations to prevent conflicts
    // but allow them if they're specifically for start position variation moves
    if (currentMoveIdx && currentMoveIdx.startsWith('-1|')) {
      // Only proceed if this analysis is specifically for a start position variation move
      // and the moveId matches the current move to prevent cross-contamination
      if (moveId !== currentMoveIdx) {
        // console.log(`⚠️ useStockfishAnalysisManager: Skipping start position variation analysis update to prevent conflicts. Current: ${currentMoveIdx}, Analysis for: ${moveId}`);
        return;
      }
    }
    
    // Only update if this is for the current move
    if (moveId === currentMoveIdx) {
      // GUARD: Don't update if we already have complete analysis for this EXACT position
      if (currentAnalysis && 
          currentAnalysis.isComplete !== false && 
          currentAnalysis.pvs?.length > 0 &&
          currentAnalysis.depth >= 15 &&
          analysisMatchesCurrentFEN(currentAnalysis, currentFEN)) {
        // console.log(`⚠️ useStockfishAnalysisManager: Skipping update - already have complete analysis for ${moveId} at depth ${currentAnalysis.depth} for FEN ${currentFEN?.substring(0, 20)}...`);
        return;
      }
      
      // If we have analysis but FEN doesn't match, log that we're updating for new position
      if (currentAnalysis && !analysisMatchesCurrentFEN(currentAnalysis, currentFEN)) {
        // console.log(`🔄 useStockfishAnalysisManager: FEN changed for move ${moveId}, allowing analysis update. Old FEN: ${(currentAnalysis.fen || currentAnalysis.positionFEN)?.substring(0, 20)}..., New FEN: ${currentFEN?.substring(0, 20)}...`);
      }
      
      setCurrentAnalysis(analysis);
      setIsAnalyzing(false);
      setAnalysisError(null);
    }
  }, [currentMoveIdx, currentAnalysis, analysisMatchesCurrentFEN, currentFEN]);
  
  // Register/unregister listener
  useEffect(() => {
    const unsubscribe = analysisManager.addListener(analysisListener);
    return unsubscribe;
  }, [analysisListener]);
  
  // Main effect: Handle currentMoveIdx changes
  useEffect(() => {
    
    
    if (!enabled || !currentMoveIdx || currentMoveIdx === lastProcessedMoveRef.current) {
      return;
    }
    
    
    lastProcessedMoveRef.current = currentMoveIdx;
    
    // Reset states
    setAnalysisError(null);
    
    // Handle starting position
    if (currentMoveIdx === '-1') {
      
      setCurrentAnalysis(analysisManager.startingPositionAnalysis);
      setIsAnalyzing(false);
      return;
    }
    
    // CRITICAL FIX: Handle starting position variations specially
    if (currentMoveIdx.startsWith('-1|')) {
      // console.log(`🔍 useStockfishAnalysisManager: Handling starting position variation ${currentMoveIdx}`);
      
      // Get the move object from starting position variations
      const moveObj = getMoveObject(currentMoveIdx, gameState);
      if (!moveObj) {
        console.warn(`⚠️ useStockfishAnalysisManager: Could not find starting position variation move ${currentMoveIdx}`);
        setCurrentAnalysis(null);
        setIsAnalyzing(false);
        return;
      }
      
      // Check if move already has analysis
      if (moveObj.analysis) {
        // GUARD: Check if analysis is complete and at desired depth
        const isCompleteAnalysis = moveObj.analysis.pvs?.length > 0 &&
                                   moveObj.analysis.isComplete !== false &&
                                   moveObj.analysis.depth >= 15;
        
        if (isCompleteAnalysis) {
          // console.log(`✅ useStockfishAnalysisManager: Using complete cached analysis for starting position variation ${currentMoveIdx} at depth ${moveObj.analysis.depth}`);
          setCurrentAnalysis(moveObj.analysis);
          setIsAnalyzing(false);
          return;
        } else {
          // console.log(`⚠️ useStockfishAnalysisManager: Cached analysis for starting position variation ${currentMoveIdx} is incomplete (depth: ${moveObj.analysis.depth || 'unknown'})`);
          // Don't clear incomplete analysis, proceed to request new analysis
        }
      }
      
      // For starting position variations, we need to calculate the FEN properly
      // Use the current FEN which should be calculated by getCurrentBoardPosition
      const fen = currentFEN;
      if (!fen) {
        console.warn(`⚠️ useStockfishAnalysisManager: No FEN available for starting position variation ${currentMoveIdx}`);
        setCurrentAnalysis(null);
        setIsAnalyzing(false);
        return;
      }
      
      // console.log(`🔍 useStockfishAnalysisManager: Requesting analysis for starting position variation ${currentMoveIdx} with FEN: ${fen}`);
      
      // Request analysis with the correct FEN
      setIsAnalyzing(true);
      setCurrentAnalysis(null);
      
      analysisManager.requestAnalysis(
        fen,
        currentMoveIdx,
        moveObj,
        updateMoveWithAnalysis
      ).catch(error => {
        if (!mountedRef.current) return;
        
        console.error(`❌ useStockfishAnalysisManager: Starting position variation analysis failed for ${currentMoveIdx}:`, error);
        setIsAnalyzing(false);
        setAnalysisError(error.message);
      });
      
      return;
    }
    
    // Get move object and FEN
    const moveObj = getMoveObject(currentMoveIdx, gameState);
    if (!moveObj) {
      console.warn(`⚠️ useStockfishAnalysisManager: Could not find move object for ${currentMoveIdx}`);
      setCurrentAnalysis(null);
      setIsAnalyzing(false);
      return;
    }
    
    // Use current FEN or derive from move
    const fen = currentFEN || moveObj.fen;
    if (!fen) {
      console.warn(`⚠️ useStockfishAnalysisManager: No FEN available for move ${currentMoveIdx}`);
      setCurrentAnalysis(null);
      setIsAnalyzing(false);
      return;
    }
    
    // Check if move already has analysis
    if (moveObj.analysis) {
      
      
      // CRITICAL FIX: Validate cached analysis before using it
      const isValidAnalysis = moveObj.analysis && 
                             moveObj.analysis.pvs && 
                             moveObj.analysis.pvs.length > 0 &&
                             moveObj.analysis.positionFEN;
      
      // GUARD: Check if analysis is complete and at desired depth
      const isCompleteAnalysis = isValidAnalysis && 
                                 moveObj.analysis.isComplete !== false &&
                                 moveObj.analysis.depth >= 15;
      
      if (isCompleteAnalysis) {
        // console.log(`✅ useStockfishAnalysisManager: Using complete cached analysis for ${currentMoveIdx} at depth ${moveObj.analysis.depth}`);
        setCurrentAnalysis(moveObj.analysis);
        setIsAnalyzing(false);
        return;
      } else if (isValidAnalysis) {
        // console.log(`⚠️ useStockfishAnalysisManager: Cached analysis for ${currentMoveIdx} is incomplete (depth: ${moveObj.analysis.depth || 'unknown'})`);
        // Don't clear incomplete analysis, just proceed to request new analysis
      } else {
        // console.log(`⚠️ useStockfishAnalysisManager: Cached analysis for ${currentMoveIdx} is invalid, clearing`);
        // Clear the bad analysis data
        updateMoveWithAnalysis(currentMoveIdx, { analysis: null });
      }
    }
    
    // Request new analysis
    
    setIsAnalyzing(true);
    setCurrentAnalysis(null);
    
    analysisManager.requestAnalysis(
      fen,
      currentMoveIdx,
      moveObj,
      updateMoveWithAnalysis
    ).catch(error => {
      if (!mountedRef.current) return;
      
      console.error(`❌ useStockfishAnalysisManager: Analysis failed for move ${currentMoveIdx}:`, error);
      setIsAnalyzing(false);
      setAnalysisError(error.message);
    });
    
  }, [currentMoveIdx, enabled, currentFEN, getMoveObject, updateMoveWithAnalysis]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Function to force analysis refresh
  const forceAnalysisRefresh = useCallback(() => {
    if (!currentMoveIdx || currentMoveIdx === '-1') {
      return;
    }
    
    
    
    const moveObj = getMoveObject(currentMoveIdx, gameState);
    const fen = currentFEN || moveObj?.fen;
    
    if (!fen || !moveObj) {
      console.warn('⚠️ useStockfishAnalysisManager: Cannot force refresh - missing FEN or move object');
      return;
    }
    
    // Clear existing analysis from move
    if (moveObj.analysis) {
      updateMoveWithAnalysis(currentMoveIdx, { analysis: null });
    }
    
    // Clear from cache
    analysisManager.setCachedAnalysis(fen, null);
    
    // Request new analysis
    setIsAnalyzing(true);
    setCurrentAnalysis(null);
    setAnalysisError(null);
    
    analysisManager.requestAnalysis(
      fen,
      currentMoveIdx,
      moveObj,
      updateMoveWithAnalysis
    ).catch(error => {
      if (!mountedRef.current) return;
      
      console.error(`❌ useStockfishAnalysisManager: Forced analysis failed:`, error);
      setIsAnalyzing(false);
      setAnalysisError(error.message);
    });
  }, [currentMoveIdx, currentFEN, getMoveObject, updateMoveWithAnalysis]);
  
  // Function to cancel current analysis
  const cancelAnalysis = useCallback(() => {
    
    analysisManager.cancelCurrentAnalysis();
    setIsAnalyzing(false);
  }, []);
  
  // Function to get manager state
  const getManagerState = useCallback(() => {
    return analysisManager.getState();
  }, []);
  
  // Debug logging for return values
  if (currentMoveIdx && currentMoveIdx.startsWith('-1|')) {
    // console.log(`🔍 useStockfishAnalysisManager: Returning for starting position variation ${currentMoveIdx}:`, {
    //   hasAnalysis: !!currentAnalysis,
    //   isAnalyzing,
    //   analysisStructure: currentAnalysis ? Object.keys(currentAnalysis) : null,
    //   hasPvs: currentAnalysis?.pvs?.length > 0,
    //   hasLines: currentAnalysis?.lines?.length > 0
    // });
  }

  return {
    // Analysis data
    analysis: currentAnalysis,
    isAnalyzing,
    analysisError,
    
    // Control functions
    forceAnalysisRefresh,
    cancelAnalysis,
    getManagerState,
    
    // Manager instance (for advanced usage)
    manager: analysisManager
  };
};

export default useStockfishAnalysisManager; 